'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import config from '@packages/config';
import type { CustomerDataWithStatus } from '@packages/shared/src/hooks/useCustomer/useCustomer';
import { useCustomer } from '@packages/shared/src/hooks/useCustomer/useCustomer';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Input } from '@packages/shared/src/ui/Input/Input';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type {
  GTMEventGlycerinDisplayNewsletterConfirm,
  GTMEventGlycerinFormSubmitClick,
} from '@packages/tracking/src/types/events';
import { usePathname } from 'next/navigation';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import type { IntlShape } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';
import * as z from 'zod';
import { ActivityOverlay } from '../ActivityOverlay.client';
import { useSubscribeNewsletterGqlMutation } from '../helpers/useSubscribeNewsletterGqlMutation';
import { formMessages, generalFormMessages } from '../messages';
import { CheckboxIDs, InputIDs } from '../types';
import { useNlSheetTexts } from './helpers/useNlSheetTexts';
import { subscribeSheetMessages } from './NewsletterSheet.messages';
import { TermsAccordion } from './TermsAccordion.client';

const newsletterSubscribeFormSchema = (formatMessage: IntlShape['formatMessage']) =>
  z.object({
    [InputIDs.EMAIL]: z
      .string()
      .nonempty({ message: formatMessage(formMessages[InputIDs.EMAIL].error) })
      .email(formatMessage(formMessages[InputIDs.EMAIL].error.valid)),
    [CheckboxIDs.APPROVAL]: z.boolean().optional(),
  });

const newsletterSubscribeFormSchemaWithApproval = (formatMessage: IntlShape['formatMessage']) =>
  z.object({
    [InputIDs.EMAIL]: z
      .string()
      .nonempty({ message: formatMessage(formMessages[InputIDs.EMAIL].error) })
      .email(formatMessage(formMessages[InputIDs.EMAIL].error.valid)),
    [CheckboxIDs.APPROVAL]: z.boolean({ required_error: '' }).refine((val) => val),
  });

const getDefaultValues = (customer: CustomerDataWithStatus | undefined) => ({
  [InputIDs.EMAIL]: customer?.email ?? '',
  [CheckboxIDs.APPROVAL]: false,
});

const {
  forms: {
    newsletterSubscribe,
    newsletterSettings: { useApproval },
  },
} = config;

const ChunkA = (unsubscribeLink: string) => (chunks: ReactNode) => (
  <a href={unsubscribeLink}>{chunks}</a>
);

export const Page1Form = ({
  onSuccess,
  closeNlSheet,
  onUserKnown,
}: {
  onSuccess: () => void;
  onUserKnown: () => void;
  closeNlSheet?: () => void;
}) => {
  const { formatMessage } = useIntl();
  const { language, defaultLanguage } = useI18n();
  const { nlTexts } = useNlSheetTexts();
  const { customer } = useCustomer();
  const dispatchGTMEvent = useTracking();
  const { isMobile } = useDeviceType();
  const scheme = useApproval
    ? newsletterSubscribeFormSchemaWithApproval(formatMessage)
    : newsletterSubscribeFormSchema(formatMessage);
  const pathname = usePathname();
  const { handleSubmit, register, formState, getValues } = useForm<z.infer<typeof scheme>>({
    resolver: zodResolver(scheme),
    mode: 'onTouched',
    defaultValues: getDefaultValues(customer),
  });
  const { errors, isValid, isSubmitting } = formState;

  const { addNotification } = useNotification();
  const doSubscribeNewsletterGql = useSubscribeNewsletterGqlMutation();

  const onSubmit = (data: z.infer<typeof scheme>) => {
    dispatchGTMEvent<GTMEventGlycerinFormSubmitClick>({
      event: 'FormSubmitClick',
      FormSubmitClickData: {
        category: 'newsletter-sheet',
        label: 'submitbutton',
      },
    });
    doSubscribeNewsletterGql({
      formData: {
        email: data.email,
      },
      areaKey: 'slideinPromotionFooter',
      contextUri: pathname ?? '/',
    })
      .then(({ data: resultData, error: resultError }) => {
        if (resultError) {
          throw new Error(resultError.message);
        }
        if (
          resultData?.subscribeNewsletterRecipient &&
          'errors' in resultData.subscribeNewsletterRecipient &&
          resultData.subscribeNewsletterRecipient.errors &&
          resultData.subscribeNewsletterRecipient.errors.length > 0
        ) {
          throw new Error(formatMessage(generalFormMessages.errorApi));
        }
        if (
          resultData?.subscribeNewsletterRecipient &&
          'alreadySubscribed' in resultData.subscribeNewsletterRecipient &&
          resultData?.subscribeNewsletterRecipient?.alreadySubscribed
        ) {
          onUserKnown();
          return;
        }
        onSuccess();
        dispatchGTMEvent({
          event: 'ga_event',
          eventCategory: 'User Interactions',
          eventLabel: 'newsletter',
          eventAction: 'subscribe',
        });
        if (getValues('email')) {
          dispatchGTMEvent<GTMEventGlycerinDisplayNewsletterConfirm>({
            event: 'DisplayNewsletterConfirm',
            DisplayNewsletterConfirmData: {
              email: getValues('email'),
              custom: { areaKey: 'slideinPromotionFooter' },
            },
          });
        }
      })
      .catch((error) =>
        addNotification({ message: error.message, severity: 'error', duration: 2500 }),
      );
  };

  /**
   * Gather the basic props (that only depend on the id) for each input
   */
  const getFieldProps = (inputId: 'email' | CheckboxIDs.APPROVAL) => ({
    ...register(inputId),
    id: inputId,
    helperText: errors[inputId]?.message,
    error: !!errors[inputId],
  });

  const buttonText =
    (nlTexts && (isMobile ? nlTexts.field_button_label_mobile : nlTexts.field_button_label)) ||
    formatMessage(isMobile ? subscribeSheetMessages.buttonMobile : subscribeSheetMessages.button);

  const configLanguage = language ?? defaultLanguage;
  const unsubscribeLink = newsletterSubscribe[configLanguage]?.unsubscribeLink;

  return (
    <ActivityOverlay showLoader={isSubmitting}>
      <div className="mx-auto my-0 max-w-2xl p-2">
        <p className="space-between text-size-headline-base mb-2.5 flex">
          {(nlTexts && nlTexts.field_headline) ||
            formatMessage(subscribeSheetMessages.formHeadline)}
          {!!closeNlSheet && isMobile && (
            <Button
              id="nl-sheet-close-button"
              variant="icon-only"
              className="bg-color-transparent border-none bg-none"
              onClick={closeNlSheet}
            >
              <span className="icon-emp-cancel" />
            </Button>
          )}
        </p>

        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
            <div>
              <Input
                type="text"
                className="bg-[white]"
                {...getFieldProps(InputIDs.EMAIL)}
                placeholder={formatMessage(subscribeSheetMessages.emailPlaceholder)}
              />
            </div>
            <div>
              <Button disabled={!isValid} size="medium" color="primary" type="submit">
                {buttonText}
              </Button>
            </div>
          </div>

          <div className="text-size-body-sm [&_a]:text-color-text-dark mt-1 mb-2">
            {useApproval && (
              // eslint-disable-next-line jsx-a11y/label-has-associated-control -- label is associated with Checkbox
              <label className="flex items-start">
                <input type="checkbox" {...getFieldProps(CheckboxIDs.APPROVAL)} />
                <span>
                  <FormattedMessage
                    key="nl-sheet-description-with-approval"
                    id={subscribeSheetMessages.formDescription.id}
                    defaultMessage={subscribeSheetMessages.formDescription.defaultMessage}
                    values={{
                      a: ChunkA(unsubscribeLink),
                    }}
                  />
                </span>
              </label>
            )}
            {!useApproval && (
              <FormattedMessage
                key="nl-sheet-description"
                id={subscribeSheetMessages.formDescription.id}
                defaultMessage={subscribeSheetMessages.formDescription.defaultMessage}
                values={{
                  a: ChunkA(unsubscribeLink),
                }}
              />
            )}
          </div>

          <TermsAccordion />
        </form>
      </div>
    </ActivityOverlay>
  );
};
